import { z } from 'zod';
/**
 * The three starting classes. Data, not code (CLAUDE.md rule 4): the server
 * validates this table at boot, and every formula reads from it rather than
 * hard-coding numbers.
 */
export const CLASS_IDS = ['warrior', 'archer', 'mage'];
export const ClassIdSchema = z.enum(CLASS_IDS);
/**
 * Classes a player may create today. One for now, by decision: the Archer and
 * the Mage keep their data, bodies and kits, but stay out of the creation
 * screen until the Warrior's game is whole. A stored character of another
 * class still loads (`ClassIdSchema`); only creation is narrowed.
 */
export const PLAYABLE_CLASS_IDS = ['warrior'];
export const PlayableClassIdSchema = z.enum(PLAYABLE_CLASS_IDS);
export const ClassDefinitionSchema = z.object({
    id: ClassIdSchema,
    /** Display name, French like all player-facing text. */
    name: z.string().min(1),
    /** Logical body sprite key in the asset manifest. */
    bodyKey: z.string().min(1),
    /**
     * Walking speed in tiles per second. The server paces movement by it and
     * rejects anything faster, so it is a rule, not a rendering hint.
     */
    tilesPerSecond: z.number().positive().max(20),
    /**
     * The naked character at level 1: health, and damage per second (ADR 0015).
     * Both grow with the level (`rules/stats.ts`); the equipment adds on top.
     */
    baseStats: z.object({
        health: z.number().int().positive(),
        dps: z.number().int().positive(),
    }),
    /**
     * Item template ids a new character is handed: its weapon and its off-hand.
     * Named here rather than inferred from the item table, so adding a cheap
     * level-1 weapon (a wooden sword) never changes what everyone starts with.
     */
    startingKit: z.tuple([z.string().min(1), z.string().min(1)]),
});
const DEFINITIONS = {
    warrior: {
        id: 'warrior',
        name: 'Guerrier',
        bodyKey: 'body.warrior',
        // 4.4, a tenth quicker than the first 4: the world grew to 384 tiles (ADR 0028).
        tilesPerSecond: 4.4,
        baseStats: { health: 120, dps: 12 },
        startingKit: ['sword-short', 'shield-wooden'],
    },
    archer: {
        id: 'archer',
        name: 'Archer',
        bodyKey: 'body.archer',
        tilesPerSecond: 4.5,
        baseStats: { health: 90, dps: 13 },
        startingKit: ['bow-short', 'quiver-leather'],
    },
    mage: {
        id: 'mage',
        name: 'Mage',
        bodyKey: 'body.mage',
        tilesPerSecond: 3.8,
        baseStats: { health: 75, dps: 14 },
        startingKit: ['wand-wooden', 'grimoire-worn'],
    },
};
/** Parsed once at module load: a malformed table fails the server at boot, not mid-game. */
export const CLASSES = Object.freeze(Object.fromEntries(CLASS_IDS.map((id) => [id, ClassDefinitionSchema.parse(DEFINITIONS[id])])));
export function getClass(id) {
    return CLASSES[id];
}
/** The class a guest starts with until class selection ships in Phase 2. */
export const DEFAULT_CLASS_ID = 'warrior';
//# sourceMappingURL=classes.js.map